import { Badge, Button, Layout } from "antd";
import { FaBell } from "react-icons/fa";
import { FaCartShopping } from "react-icons/fa6";
import { FaUserAlt } from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useApiCall } from "../services/api";
import { useDispatch, useSelector } from "react-redux";
import SearchPopover from "./popover/SearchPopover";
import CreateUserFlow, { SelectNewUser } from "./models/CreateUserFlow";
import PrimaryButton from "./Forms/PrimaryButton";
import { changeCurrentMenu, setCounts, setOfferList } from "../redux/auth/navCountSlice";
import { APP_VERSION, roleIds } from "../constant";
import AppLogo from "./AppLogo";
import { handleRouteNav } from "../utils";
import NewAnnouncement from "./models/NewAnnouncement";
import { useAllRoutes } from "../routes/AppRoutes";
import AddSchemeModal from "./models/AddSchemeModal";
import { listRefresh, notificationRefresh } from "../redux/auth/cartRefreshSlice";
import { clearStoredPaginationAndFilters } from "./SideBar";

const { Header } = Layout;
const headerStyle = {
  textAlign: "center",
  color: "#fff",
  height: "fit-content",
  padding: "40px 40px 0 40px",
  background: "transparent",
};

function HeaderNav() {
  const { pathname } = useLocation();
  const allRoutes = useAllRoutes();
  const currentMenuItem = allRoutes.find((item) => item.path === pathname);
  const isExtraActiveLink = pathname.startsWith("/home");
  const productActiveLink = pathname.startsWith("/product-details");
  const dispatch = useDispatch();
  const navigationType = useNavigationType();

  const routes = [
    {
      key: 1,
      parentRoute: "/",
      childRoutes: ["/home/*"],
    },
    {
      key: 2,
      parentRoute: "/product-catalog",
      childRoutes: ["/product-details/*", "/product-catalog/checkout"],
    },
    {
      key: 3,
      parentRoute: "/orders",
      childRoutes: ["/orders/*"],
    },
  ];
  const prevPathRef = useRef(pathname);
  const getRouteGroup = (path) => {
    return routes.find(
      (route) =>
        path === route.parentRoute ||
        route.childRoutes.some((child) => (child.includes("*") ? path.startsWith(child.replace("/*", "")) : path === child)),
    );
  };

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = pathname;

    const prevGroup = getRouteGroup(prevPath);
    const currentGroup = getRouteGroup(currentPath);

    if (currentGroup?.parentRoute && prevGroup?.parentRoute !== currentGroup?.parentRoute) {
      dispatch(notificationRefresh());
      clearStoredPaginationAndFilters();
    }

    prevPathRef.current = currentPath;
  }, [pathname, navigationType, currentMenuItem?.path]);

  return (
    <div className="flex items-center gap-10">
      <NavLink to="/">
        <AppLogo divClassName="!w-32 transition-all" />
      </NavLink>

      <nav className="flex gap-6 items-center text-black">
        <NavLink
          to="/"
          className={({ isActive }) => (isActive || isExtraActiveLink ? "font-semibold underline text-[#A33434] focus:underline" : "text-black")}
        >
          <span className="font-abhaya text-lg">Home</span>
        </NavLink>

        <NavLink
          to="/product-catalog"
          className={({ isActive }) => (isActive || productActiveLink ? "font-semibold underline text-[#A33434] focus:underline" : "text-black")}
        >
          <span className="font-abhaya text-lg">Product Catalogue</span>
        </NavLink>

        <NavLink to="/orders" className={({ isActive }) => (isActive ? "font-semibold underline text-[#A33434] focus:underline" : "text-black")}>
          <span className="font-abhaya text-lg">My Orders</span>
        </NavLink>
      </nav>
    </div>
  );
}

const Nav = ({ title, description, backButton = false }) => {
  const { postApi, loading } = useApiCall();
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [isNewAnnouncementModalVisible, setIsNewAnnouncementModalVisible] = useState(false);
  const [isSchemeModalVisible, setSchemeModalVisible] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [formType, setFormType] = useState(null);

  const { cartItems, notificationCount } = useSelector((state) => state.navCounts);
  const { user_id, is_sales_manager, full_name, is_customer, is_admin, role_id, customer_id, is_agent } = useSelector((state) => state.auth);
  const { refreshKey, notificationRefreshKey, cartRefreshKey } = useSelector((state) => state.cartRefresh);

  const handleOk = () => {
    console.log("Form submitted");
    setIsModalVisible(false);
  };

  const handleNewAnnouncementOk = async (values) => {
    try {
      const response = await postApi(
        "announcements/add",
        {
          ...values,
          announcement_roles: Array.isArray(values.announcement_roles) ? values.announcement_roles.join(",") : values.announcement_roles,
        },
        true,
      );
      if (response?.status) {
        setIsNewAnnouncementModalVisible(false);
        dispatch(listRefresh());
        dispatch(notificationRefresh());
      }

      return response?.status || false;
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = () => {
    console.log("Modal closed");
    setIsModalVisible(false);
  };

  const OpenAddClient = (type) => {
    if (type === "announcement") {
      setIsNewAnnouncementModalVisible(true);
    } else if (type === "scheme") {
      setSchemeModalVisible(true);
    } else {
      setFormType(type);
      setIsModalVisible(true);
    }
  };

  const fetchCartDetails = async () => {
    try {
      const customerId = {
        customer_id: customer_id,
      };
      const response = await postApi(
        is_customer ? "cart/get-cart-customer-id" : "sales/cart/get-cart-user-id",
        is_customer
          ? customerId
          : {
              user_id,
            },
      );
      dispatch(setCounts({ cartItems: response?.total_items || 0 }));
    } catch (error) {
      console.error("Error fetching cart details:", error);
    }
  };

  const notification = async () => {
    try {
      const payloadRoles = {
        1: "admin",
        2: "customer",
        3: "sales_person",
        4: "sales_manager",
      };

      const customerId = {
        user_id: customer_id,
        role: payloadRoles[role_id] || "customer",
      };

      const response = await postApi(
        is_agent ? "sales/common/agent-notification" : "sales/common/notification",
        is_customer
          ? customerId
          : {
              user_id: is_sales_manager || is_admin ? undefined : user_id,
              role: payloadRoles[role_id] || "admin",
            },
      );

      dispatch(setCounts({ notificationCount: response?.unread_count || 0, appVersion: response?.web_version || APP_VERSION }));
    } catch (error) {
      console.error("Error fetching cart details:", error);
    }
  };

  useEffect(() => {
    fetchCartDetails();
  }, [refreshKey, cartRefreshKey]);

  useEffect(() => {
    notification();
  }, [refreshKey, notificationRefreshKey]);

  const ROUTE_ACTIONS = {
    client: {
      paths: ["/sales/clients", "/sales/client-profile", "/sales/pending-clients"],
      label: "New Client",
      actionType: "client",
    },
    employee: {
      paths: ["/sales/sales-employees", "/sales/sales-profile"],
      label: "New Employee",
      actionType: "Employee",
    },
    user: {
      paths: ["/admin/users", "/admin/user-profile", "/admin/pending-clients", "/admin/customers", "/admin/client-profile"],
      label: "New User",
      actionType: "user",
    },
    announcement: {
      paths: ["/admin/announcements"],
      label: "New Announcement",
      actionType: "announcement",
    },

    scheme: {
      paths: [
        "/admin/discount-schemes",
        "/sales/discount-schemes",
        "/admin/scheme-requests",
        "/sales/scheme-requests",
        "/admin/scheme-details",
        "/sales/scheme-details",
      ],
      label: "New Scheme",
      actionType: "scheme",
      excludeFor: [roleIds.SalesPerson],
    },
  };

  const currentRouteAction = Object.values(ROUTE_ACTIONS).find((route) =>
    route.paths.some((path) => {
      if (route.excludeFor?.includes(role_id)) return false;
      if (path === pathname) return true;

      if (path.includes("*")) {
        const base = path.replace("/*", "");
        return pathname.startsWith(base);
      }

      return false;
    }),
  );

  return (
    <>
      <Header className="py-10 headercss" style={headerStyle}>
        <div className="flex justify-between items-start">
          <div className="flex flex-col items-start gap-6">
            {is_customer ? (
              <div className="mb-5">
                <HeaderNav />
              </div>
            ) : null}
            {["/sales/dashboard", "/admin/dashboard"].includes(pathname) ? (
              <div className="flex gap-4 items-center">
                <div className="w-[60px] h-[60px] bg-[#A334341A] rounded-xl">
                  <img src="../../assets/icons/welcome-logo.png" alt="welcome-logo" />
                </div>
                <p className="text-[#000000] text-left text-xl">
                  <strong>Hi {full_name},</strong>
                  <br />
                  how are you today?
                </p>
              </div>
            ) : (
              <div className={`flex ${description ? "items-start" : "items-center"} gap-3 mb-3`}>
                {backButton && (
                  <button onClick={() => navigate(-1)} className="focus:outline-none flex-shrink-0">
                    <img src="/assets/icons/NavBack.png" alt="Back" className={`w-auto h-[42px] object-contain ${description ? "" : ""}`} />
                  </button>
                )}
                {(title || description) && (
                  <div className="flex flex-col justify-center text-start">
                    {title && <h1 className="text-[#000000] text-[40px] font-abhaya mt-1">{title}</h1>}
                    {description && <div className="text-base text-gray-600 mt-2">{description}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {currentRouteAction ? (
              <PrimaryButton
                onClick={(e) => {
                  e.stopPropagation();
                  OpenAddClient(currentRouteAction.actionType);
                }}
                noIcon
                className="!h-[42px]"
                text={currentRouteAction.label}
              />
            ) : (
              <SearchPopover open={isSearchModalVisible} setOpen={setIsSearchModalVisible}>
                <PrimaryButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormType("Order");
                    setIsSearchModalVisible(true);
                  }}
                  text="New Order"
                  noIcon
                  className="!h-[42px]"
                />
              </SearchPopover>
            )}

            <Badge color="#484848" count={cartItems || 0}>
              <Button
                type="primary"
                className="bg-transparent text-[#484848] hover:!bg-[#A33434] border-1.5 border-[#484848]"
                shape="circle"
                icon={<FaCartShopping />}
                size={"large"}
                onClick={() => navigate(handleRouteNav(`/product-catalog/checkout`, role_id))}
              />
            </Badge>
            <Badge color="#484848" count={notificationCount || 0}>
              <Button
                type="primary"
                className="bg-transparent text-[#484848] hover:!bg-[#A33434] border-1.5 border-[#484848]"
                shape="circle"
                icon={<FaBell />}
                size={"large"}
                onClick={() => {
                  if (pathname !== handleRouteNav(`/notification`, role_id)) {
                    Object.keys(sessionStorage).forEach((key) => {
                      if (key.includes("notification") && (key.startsWith("filters:") || key.startsWith("pagination:"))) {
                        sessionStorage.removeItem(key);
                      }
                    });
                  }
                  navigate(handleRouteNav(`/notification`, role_id));
                }}
              />
            </Badge>
            <Button
              type="primary"
              onClick={() =>
                navigate(handleRouteNav(`/profile`, role_id), {
                  state: {
                    user_id: user_id,
                    routeType: is_customer ? "client" : "user",
                  },
                })
              }
              className="bg-transparent text-[#484848] hover:!bg-[#A33434] border-1.5 border-[#484848]"
              shape="circle"
              icon={<FaUserAlt />}
              size={"large"}
            />
          </div>
        </div>
      </Header>

      <CreateUserFlow
        formType={formType}
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        // only boolean to decide immediate role selector open
        openRoleSelectorOnOpen={formType === "user"}
        handleSubmit={() => dispatch(listRefresh())}
      />

      <NewAnnouncement
        isOpen={isNewAnnouncementModalVisible}
        closeModal={() => setIsNewAnnouncementModalVisible(false)}
        onConfirm={handleNewAnnouncementOk}
        loading={loading}
      />

      <AddSchemeModal
        isOpen={isSchemeModalVisible}
        closeModal={() => setSchemeModalVisible(false)}
        // onConfirm={handleNewSchemeOk}
        isEdit={false}
        handleSubmit={() => dispatch(listRefresh())}
      ></AddSchemeModal>
    </>
  );
};

export default Nav;
